import * as React from "react"
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination"
import { useTranslation } from "react-i18next"

// Extend DataTable to support row click and hover styles
// onRowClick: optional callback receiving the original row data
// clickableRows: when true, adds hover background and pointer cursor
function DataTable({ data, columns, pagination, sorting = true, onRowClick, clickableRows = false, compact = false, manualSort, onSortChange }) {
  // Use 'common' namespace for translations since this is a shared component
  const { t } = useTranslation('common')

  // Convert our { column, direction } manualSort into React Table's SortingState.
  const controlledSorting = React.useMemo(() => {
    if (!manualSort || !manualSort.column || !manualSort.direction) return []
    return [{ id: manualSort.column, desc: manualSort.direction === 'desc' }]
  }, [manualSort])

  const isManualSort = Boolean(onSortChange)

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: isManualSort ? undefined : getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableSorting: sorting,
    manualSorting: isManualSort,
    state: isManualSort ? { sorting: controlledSorting } : undefined,
    onSortingChange: isManualSort
      ? (updater) => {
          const next = typeof updater === 'function' ? updater(controlledSorting) : updater
          const first = next?.[0]
          if (first) {
            // We cycle desc → asc → null via the parent handler; pass the id only.
            onSortChange(first.id)
          } else {
            onSortChange(manualSort?.column || null)
          }
        }
      : undefined,
    initialState: {
      pagination: {
        pageSize: pagination?.pageSize || 10,
      },
    },
  })

  React.useEffect(() => {
    if (pagination?.pageSize) {
      table.setPageSize(pagination.pageSize)
      return
    }

    if (!pagination) {
      table.setPageSize(data.length || 10)
    }
  }, [pagination, pagination?.pageSize, data.length, table])

  const buildClassName = (base, extra = '') => {
    return [base, compact ? extra : ''].filter(Boolean).join(' ').trim() || undefined
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table className={compact ? 'text-xs' : undefined}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={buildClassName(header.column.columnDef.meta?.className, 'py-2')}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={[
                    clickableRows ? "hover:bg-muted/50 cursor-pointer" : '',
                    compact ? 'text-xs' : ''
                  ].filter(Boolean).join(' ') || undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={buildClassName(cell.column.columnDef.meta?.className, 'py-2')}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {pagination && (
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2 flex-shrink-0">
            <p className="text-sm font-medium whitespace-nowrap">
              {t('pagination.rowsPerPage')}
            </p>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value))
              }}
              className="h-8 w-[70px] rounded-md border border-input bg-background px-3 py-1 text-sm"
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </div>
          
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  {t('pagination.previous')}
                </Button>
              </PaginationItem>
              
              {(() => {
                const currentPage = table.getState().pagination.pageIndex + 1;
                const totalPages = table.getPageCount();
                const pages = [];
                
                // Always show first page
                if (totalPages > 0) {
                  pages.push(1);
                }
                
                // Show pages around current page
                const startPage = Math.max(2, currentPage - 1);
                const endPage = Math.min(totalPages - 1, currentPage + 1);
                
                if (startPage > 2) {
                  pages.push('...');
                }
                
                for (let i = startPage; i <= endPage; i++) {
                  if (i !== 1 && i !== totalPages) {
                    pages.push(i);
                  }
                }
                
                if (endPage < totalPages - 1) {
                  pages.push('...');
                }
                
                // Always show last page
                if (totalPages > 1) {
                  pages.push(totalPages);
                }
                
                return pages.map((page, index) => {
                  if (page === '...') {
                    return (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <span className="px-2">...</span>
                      </PaginationItem>
                    );
                  }
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => table.setPageIndex(page - 1)}
                        isActive={page === currentPage}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                });
              })()}
              
              <PaginationItem>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  {t('pagination.next')}
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  )
}

export { DataTable }

